import SectionHeading from '@/pages/home/components/SectionHeading';
import useScrollReveal from '@/hooks/useScrollReveal';
import type { ArchiveItem } from '@/pages/home/types';

interface ArchiveSectionProps {
  items: ArchiveItem[];
  topicName: string;
}

export default function ArchiveSection({ items, topicName }: ArchiveSectionProps) {
  const { ref, visible } = useScrollReveal();

  return (
    <section id="archive" className="w-full bg-background-100 py-16 md:py-24 scroll-mt-24">
      <div ref={ref} className={`w-full px-4 md:px-8 nh-reveal ${visible ? 'nh-in' : ''}`}>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            kicker="素材归档"
            title="封面驱动的归档卡片，先看形态再看内容"
            desc="每条归档都标注素材形态、核验状态与编号，方便回溯来源。当前筛选话题："
          />
          <span className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-100 text-secondary-900 text-sm whitespace-nowrap">
            <i className="ri-filter-3-line"></i>
            {topicName} · 共 {items.length} 条
          </span>
        </div>

        {items.length === 0 ? (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="mx-auto flex w-12 h-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-inbox-archive-line text-xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">该话题下的素材仍在整理中，先看看其它话题的归档吧。</p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {items.map((item) => (
              <article
                key={item.id}
                className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 transition-colors hover:border-primary-300"
              >
                <div className="relative w-full h-52 md:h-56">
                  <img src={item.cover} alt={`${item.category}素材封面，${item.title}`} className="w-full h-full object-cover object-top" />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-50/92 text-primary-700 text-xs font-medium">
                    <i className="ri-bookmark-3-line"></i>
                    {item.category}
                  </div>
                  <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-foreground-950/70 text-background-50 text-xs">
                    <i className="ri-time-line"></i>
                    {item.duration}
                  </div>
                </div>

                <div className="flex flex-col flex-1 p-5">
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600 flex-1">{item.summary}</p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {item.labels.map((label) => (
                      <span key={label} className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] font-label">
                        {label}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 pt-4 border-t border-background-200 flex items-center justify-between">
                    <span className="text-xs text-foreground-500">{item.meta}</span>
                    <span className="inline-flex items-center gap-1 text-xs text-primary-600 group-hover:gap-2 transition-all">
                      查看归档
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}